import { FormBuilderSections } from '@folio/components';
import { useTranslation } from 'react-i18next';
import type { AvailablePricingPeril } from './use-available-pricing';
import type { Field, Fields, RadioField } from 'korint-sdk/react';

type PerilOptionsCardProps = {
  productId: string;
  perils: AvailablePricingPeril[];
  perilFields: Fields;
};

const isRadioBooleanField = (f: Field): f is RadioField =>
  f.type === 'radio' &&
  'options' in f &&
  Array.isArray(f.options) &&
  f.options.length === 2 &&
  f.options.some(o => o === 'true' || (typeof o === 'object' && o.value === 'true'));

export function PerilOptionsCard({ perils, perilFields }: Readonly<PerilOptionsCardProps>) {
  const { t } = useTranslation();

  if (perilFields.length === 0) return null;

  const perilDefaultsByField = Object.fromEntries(
    perils.flatMap(p =>
      (p.customFields ?? []).map(cf => {
        const defaultVal = p.defaultCustomFieldValues?.find(v => v.key === cf)?.value;
        const value =
          typeof defaultVal === 'boolean' ? String(p.included) : (defaultVal ?? String(p.included));
        return [cf, value];
      }),
    ),
  );

  return (
    <FormBuilderSections
      sections={[
        {
          fields: perilFields.map(f => {
            const defaultValue = perilDefaultsByField[f.name];
            if (isRadioBooleanField(f)) {
              return {
                ...f,
                defaultValue,
                type: 'select',
              };
            }
            return { ...f, defaultValue };
          }),
          title: t('perilOptionsCard.title'),
        },
      ]}
    />
  );
}
